// @flow

import React, { PureComponent } from 'react';
import glamorous from 'glamorous';
import { css } from 'glamor';
import numeral from 'numeral';
import colorUtils from 'color';
import Tappable from 'app/components/commons/Tappable';
import * as cssEase from 'app/constants/cssEase';
import Avatar from 'app/components/commons/Avatar';
import getGlobalLeaderboardScale from 'app/utils/getGlobalLeaderboardScale';
import type { OriginalEntry } from 'app/normalizers/decodeUltimateChallenge';
import AnimatedGroup from 'app/components/commons/AnimatedGroup';
import * as Enter from 'app/utils/animations/enter';
import * as Leave from 'app/utils/animations/leave';
import { hover } from 'app/utils/cssUtils';
import vipIcon from 'app/images/globalLeaderboard/blue-tick.svg';
import LeaderboardRowDetails from './LeaderboardRowDetails';

type Props = {
  avatar: Object,
  color: string,
  countryName?: string,
  delay: number,
  displayCountry: boolean,
  flagImage?: string,
  globalScore: number,
  highestScore: number,
  id: number,
  isOpen: boolean,
  isUltimateChampion: boolean,
  isVIP: boolean,
  lowScore: number,
  name: string,
  onClick: (id: number) => void,
  originalEntries: Array<OriginalEntry>,
  rank: number,
  prize?: string,
};

const LeaderboardRowDiv = glamorous.div(props => ({
  width: '100%',
  position: 'relative',
  borderBottom: '1px solid',
  borderColor: colorUtils(props.color)
    .alpha(0.3)
    .string(),
}));

const ClickArea = glamorous.div(props => ({
  alignItems: 'stretch',
  display: 'flex',
  flexGrow: 1,
  justifyContent: 'space-between',
  padding: props.theme.spacing.small,
  position: 'relative',
  ...hover({
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  }),
  width: '100%',
}));

const scaleIn = css.keyframes({
  from: { transform: 'scaleX(0)' },
  to: { transform: 'scaleX(1)' },
});

const ColorDiv = glamorous.div(
  {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: '100%',
    transformOrigin: 'left',
    transform: 'scaleX(0)',
    animation: `${scaleIn} 1s ${cssEase.Strong.easeOut}`,
    animationFillMode: 'forwards',
  },
  props => ({
    animationDelay: `${props.delay}s`,
    width: `${props.width}%`,
    backgroundColor: props.color,
  }),
);

const Text = glamorous.div(({ bold, theme }) => ({
  fontWeight: theme.fontWeight.normal,
  fontSize: bold ? theme.fontSize.default : theme.fontSize.tiny,
  opacity: bold ? 1 : 0.7,
}));

const TextWrapper = glamorous.div(props => ({
  paddingLeft: props.theme.spacing.medium,
  paddingRight: props.theme.spacing.medium,
}));

const Rank = glamorous.div(props => ({
  fontWeight: props.theme.fontWeight.normal,
  paddingLeft: props.theme.spacing.small,
  paddingRight: props.theme.spacing.small,
  width: 55,
}));

const DropDownContainer = glamorous.div(
  {
    borderBottom: '1px solid',
    transform: 'translateZ(0)',
  },
  props => ({
    borderColor: colorUtils(props.color)
      .alpha(0.3)
      .string(),
    backgroundColor: colorUtils(props.color)
      .alpha(0.6)
      .string(),
  }),
);

const GenericDiv = glamorous.div({
  alignItems: 'center',
  display: 'flex',
});

const StyledLeaderboardRowDetails = glamorous(LeaderboardRowDetails)(props => ({
  position: 'relative',
  paddingLeft: props.theme.spacing.small,
  paddingRight: props.theme.spacing.small,
  justifyContent: 'space-between',

  [props.theme.mediaQuery.desktopAndUp]: {
    justifyContent: 'flex-start',
  },
}));

const AvatarContainer = glamorous.div({
  position: 'relative',
});

class LeaderboardRow extends PureComponent<Props, any> {
  props: Props;

  static defaultProps = {
    countryName: undefined,
    flagImage: undefined,
    prize: undefined,
  };

  handleClick = () => {
    const { id, onClick } = this.props;
    onClick(id);
  };

  render() {
    const {
      avatar,
      color,
      countryName,
      delay,
      displayCountry,
      flagImage,
      globalScore,
      highestScore,
      isOpen,
      isUltimateChampion,
      isVIP,
      lowScore,
      name,
      originalEntries,
      rank,
      prize,
    } = this.props;

    return (
      <div>
        <LeaderboardRowDiv color={color}>
          <ColorDiv
            delay={delay}
            color={color}
            width={getGlobalLeaderboardScale(globalScore, lowScore, highestScore) * 100}
          />
          <Tappable component={ClickArea} onTap={this.handleClick}>
            <GenericDiv>
              <Rank>#{rank}</Rank>
              <AvatarContainer>
                <Avatar
                  avatar={avatar}
                  countryFlag={displayCountry ? flagImage : undefined}
                  hasCrown={isUltimateChampion}
                  size="xsmall"
                />
              </AvatarContainer>
              <TextWrapper>
                <Text bold={!prize}>
                  {name}
                  {isVIP && (
                    <span>
                      {' '}
                      <img src={vipIcon} alt="" />
                    </span>
                  )}
                </Text>
                <Text bold={prize}>{prize || countryName}</Text>
              </TextWrapper>
            </GenericDiv>
            <GenericDiv>{numeral(globalScore).format('0.00')}</GenericDiv>
          </Tappable>
        </LeaderboardRowDiv>
        <AnimatedGroup onEnter={Enter.grow(0.3)} onLeave={Leave.shrink(0.3)}>
          {isOpen && (
            <DropDownContainer key="dropdown" color={color}>
              {originalEntries.map((entry, i) => (
                <StyledLeaderboardRowDetails
                  key={entry.id}
                  challengeId={entry.challengeId}
                  globalScore={entry.globalScore}
                  match={entry.challengeName}
                  order={i + 1}
                />
              ))}
            </DropDownContainer>
          )}
        </AnimatedGroup>
      </div>
    );
  }
}

export default LeaderboardRow;
